import { useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  DaywiseOrder,
  DaywiseOrderReportResponse,
  Pagination,
  RevenueSummaryResponse,
} from "../Orders/Types";
import { OrderService } from "../../../services/api/order.service";
import { DataTable } from "../../DataTable/DataTable";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

interface ClinicList {
  id: string;
  name: string;
  profileImage: string;
}

interface Filters {
  startDate: string;
  endDate: string;
  clinicId: string;
}

const OnlineDaywiseReport = ({ isOnline }: { isOnline: boolean }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DaywiseOrder[]>([]);
  const [revenueSummary, setRevenueSummary] = useState<{
    total_orders: number;
    total_amount: number;
    cash_earning: number;
    online_earning: number;
  } | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 10,
    totalItemsCount: 0,
    totalPages: 0,
  });
  const formatDate = (date: Date) => date.toISOString().split("T")[0];
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 30);

  const initialStartDate = formatDate(yesterday);
  const initialEndDate = formatDate(today);
  const [filters, setFilters] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate,
    clinicId: user?.role === "clinic" ? user?.id : "",
  });
  const [clinicList, setClinicList] = useState<ClinicList[]>([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      startDate: initialStartDate,
      endDate: initialEndDate,
    }));
  }, []);
  const getDaywiseReport = async () => {
    try {
      setLoading(true);
      const res: DaywiseOrderReportResponse = isOnline
        ? await OrderService.getOnlineDaywiseReport(
          filters,
          pagination.page,
          pagination.pageSize,
        )
        : await OrderService.getOfflineDaywiseReport(
          filters,
          pagination.page,
          pagination.pageSize,
        );

      const summaryRes: RevenueSummaryResponse = isOnline
        ? await OrderService.getOnlineRevenueSummary(filters)
        : await OrderService.getOfflineRevenueSummary(filters);

      if (res.success) {
        setData(res.data.data);
        setPagination(res.data.pagination);
      }

      if (summaryRes && summaryRes.success) {
        setRevenueSummary(summaryRes.data);
      } else {
        setRevenueSummary(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const getClinicList = async () => {
    const res = await OrderService.getClinicList();
    setClinicList(res.data);
  };

  useEffect(() => {
    getClinicList();
  }, []);

  useEffect(() => {
    getDaywiseReport();
  }, [filters, pagination.page, pagination.pageSize, isOnline]);

  const handlePageChange = (page: number) =>
    setPagination((p) => ({ ...p, page }));

  const handlePageSizeChange = (pageSize: number) =>
    setPagination((p) => ({ ...p, pageSize, page: 1 }));

  const updateFilter = (key: keyof Filters, value: string) => {
    setPagination((p) => ({ ...p, page: 1 }));
    setFilters((prev) => ({ ...prev, [key]: value }));
  };
  const handleExportToExcelInChunks = async () => {
    setDownloading(true);

    try {
      const allRows: any[] = [];
      const pageSize = 20;
      let page = 1;

      while (true) {
        const res: DaywiseOrderReportResponse = isOnline
          ? await OrderService.getOnlineDaywiseReport(filters, page, pageSize)
          : await OrderService.getOfflineDaywiseReport(filters, page, pageSize);

        const data = res?.data?.data || [];

        if (!data.length) break;

        const rows = data.map((item: DaywiseOrder) => ({
          "Order Date": item?.order_date
            ? new Date(item.order_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            : "N/A",

          // Keep these as actual numbers
          "Total Orders": Number(item?.total_orders) || 0,
          "Cash Earnings": Number(item?.cash_earning) || 0,
          "Online Earnings": Number(item?.online_earning) || 0,
          "Total Amount": Number(item?.total_earning) || 0,
        }));

        allRows.push(...rows);

        if (data.length < pageSize) break;

        page++;
      }

      if (!allRows.length) {
        toast.error("No data found");
        return;
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(allRows);

      // Format numeric columns
      // A = Order Date
      // B = Total Orders
      // C = Cash Earnings
      // D = Online Earnings
      // E = Total Amount

      allRows.forEach((_, index) => {
        const row = index + 2;

        // Total Orders → integer
        const totalOrders = worksheet[`B${row}`];
        if (totalOrders) {
          totalOrders.t = "n";
          totalOrders.z = "0";
        }

        // Earnings → 2 decimal places
        ["C", "D", "E"].forEach((column) => {
          const cell = worksheet[`${column}${row}`];

          if (cell) {
            cell.t = "n";
            cell.z = "0.00";
          }
        });
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Daywise Order Report"
      );

      // Download
      XLSX.writeFile(
        workbook,
        "daywise_order_report.xlsx"
      );
    } catch (error) {
      console.error("Error exporting daywise order report:", error);
      toast.error("Error while exporting the daywise order report");
    } finally {
      setDownloading(false);
    }
  };
  const columns = [
    {
      key: "orderDate",
      title: "Order Date",
      render: (item: DaywiseOrder) => (
        <div>
          {new Date(item?.order_date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }) || "N/A"}
        </div>
      ),
    },
    {
      key: "totalOrders",
      title: "Total Orders",
      render: (item: DaywiseOrder) => <div>{item?.total_orders || "0"}</div>,
    },
    {
      key: "cashEarnings",
      title: "Cash Earnings",
      render: (item: DaywiseOrder) => <div>{item?.cash_earning || "0"}</div>,
    },
    {
      key: "onlineEarnings",
      title: "Online Earnings",
      render: (item: DaywiseOrder) => <div>{item?.online_earning || "0"}</div>,
    },
    {
      key: "totalAmount",
      title: "Total Amount",
      render: (item: DaywiseOrder) => <div>{item?.total_earning || "0"}</div>,
    },
    //     ...(user?.role !== "clinic"
    //       ? [
    //           {
    //             key: "Clinic Name",
    //             title: "Clinic Name",
    //             sortable: true,
    //             width: "w-[250px]",
    //             render: (item: OfflineOrder) => (
    //               <div className="flex items-center">
    //                 <img
    //                   src={item?.clinic?.clinicProfileImage || avatarPlaceholder}
    //                   alt={item?.clinic?.clinicName}
    //                   className="w-8 h-8 rounded-full mr-3 object-cover border border-gray-200"
    //                 />
    //                 <div>
    //                   <div className="font-medium text-gray-900">
    //                     {item?.clinic?.clinicName}
    //                   </div>
    //                 </div>
    //               </div>
    //             ),
    //           },
    //         ]
    //       : []),
  ];

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-end justify-end"></div>

      {/* SUMMARY CARDS */}
      {revenueSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col items-center justify-center">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Total Orders</h3>
            <p className="text-xl font-bold text-gray-800">{revenueSummary.total_orders}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col items-center justify-center">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Total Amount</h3>
            <p className="text-xl font-bold text-gray-800">₹{revenueSummary.total_amount}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col items-center justify-center">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Cash Earnings</h3>
            <p className="text-xl font-bold text-gray-800">₹{revenueSummary.cash_earning}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col items-center justify-center">
            <h3 className="text-sm font-medium text-gray-500 mb-1">Online Earnings</h3>
            <p className="text-xl font-bold text-gray-800">₹{revenueSummary.online_earning}</p>
          </div>
        </div>
      )}

      {/* FILTERS */}
      <div
        className={`grid grid-cols-1 gap-4 bg-white p-4 rounded-lg border ${user?.role === "clinic" ? "md:grid-cols-5" : "md:grid-cols-6"
          }`}
      >
        {/* From Date */}
        <input
          type="date"
          className="border rounded px-3 py-2"
          value={filters.startDate}
          onChange={(e) => updateFilter("startDate", e.target.value)}
        />

        {/* To Date */}
        <input
          type="date"
          className="border rounded px-3 py-2"
          value={filters.endDate}
          onChange={(e) => updateFilter("endDate", e.target.value)}
        />

        {/* Clinic */}
        {user?.role !== "clinic" && (
          <select
            className="border rounded px-3 py-2"
            value={filters.clinicId}
            onChange={(e) => updateFilter("clinicId", e.target.value)}
          >
            <option value="">All Clinics</option>
            {clinicList.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>
                {clinic.name}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={handleExportToExcelInChunks}
          className="px-3 py-2 left-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
          disabled={downloading}
        >
          {downloading ? "Exporting..." : "Export to Excel"}
        </button>
      </div>

      {/* TABLE */}
      <DataTable
        data={data}
        columns={columns}
        itemsPerPage={pagination.pageSize}
        isLoading={loading}
        pagination={{
          currentPage: pagination.page,
          setCurrentPage: handlePageChange,
          totalPages: pagination.totalPages,
          totalItemsCount: pagination.totalItemsCount,
          pageSize: pagination.pageSize,
          setPageSize: handlePageSizeChange,
        }}
      />
    </div>
  );
};

export default OnlineDaywiseReport;
